import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

import { useTranslation } from 'react-i18next';
import TableContainer from '@material-ui/core/TableContainer';
import MuiTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';

import pushToGTM, { CLICK_DATASHEET_GA4_TAG } from '../../utils/analytics/gtm';
import { DIMENSIONS } from '../../styles/constants';

import { getDescription, getSorting, stableSort, sumColumnValues } from './utils';
import { useStyles } from './styles';
import Paper from '../Paper';
import Link from '../Link';
import TableHead from './TableHead';
import { logEventType, middlePartOfRedirectLink } from '../../utils/analytics/adobe/adob';
import queryString from 'query-string';
import { ReactComponent as SimilarProductsIcon } from '../../images/icons/similarProductsIcon.svg';
import { LinkContainer } from './styles';
import { localClickOnSimilarProducts } from '../../utils/analytics/local';
import { adobeOpenSimilarProducts } from '../../utils/analytics/adobe/adob';
import BlurWrapper from '../BlurWrapper';

const Table = ({ columns, title, actions, footerContent, withTotalRow, items, logIn, ...rest }) => {
	const { t, i18n } = useTranslation();
	const classes = useStyles({ withTotalRow });
	const [order, setOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState('');
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const userLoggedIn = useSelector(state => state.user.loggedIn);

	const totalRowValues = sumColumnValues(columns, items);

	const domain = window.location.origin;
	const fullURL = window.location.href;
	const path = window.location.pathname;

	const datasheetTrackingParameters = item => {
		return {
			redirectLink: item.datasheetLink,
			partNumber: item['partNumber'],
			inputParameters: JSON.stringify(inputParametersDL),
			buttonName: item['partNumber'],
			wdtTabName: 'summary',
			fullURL: fullURL,
			path: path,
			logEventType: logEventType.DATASHEETLOG
		};
	};

	const handleRequestSort = (_event, property) => {
		const isDesc = orderBy === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderBy(property);
	};

	return (
		<Paper padding={withTotalRow ? [1, 2, 0, 2] : [1, 2, 2, 2]}>
			<Toolbar className={classes.toolbar}>
				<Grid container justify={title ? 'space-between' : 'flex-end'} spacing={2}>
					{title &&
						(progressActive ? (
							<Skeleton height={DIMENSIONS.SKELETON_TITLE_HEIGHT} />
						) : (
							<Grid item>
								<Typography variant="h6">{title}</Typography>
							</Grid>
						))}

					<Grid item xs={!title} className={!title && classes.actionsContainer}>
						{actions}
					</Grid>
				</Grid>
			</Toolbar>

			<TableContainer>
				<BlurWrapper isBlurred={!userLoggedIn} showLoginButton={true} logIn={logIn}>
					<MuiTable aria-labelledby="tableTitle" {...rest}>
						<TableHead columns={columns} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />

						<TableBody>
							{stableSort(items, getSorting(order, orderBy)).map(item => {
								return (
									<TableRow key={'table-row-item-' + item.part} className={classes.tableRow}>
										{columns.map(column => (
											<TableCell key={'table-cell-' + column.key} align={'left'}>
												{column.key === 'attributes' ? (
													<List disablePadding>
														{item[column.key].map(attribute => (
															<ListItem key={'bom-item-attribute-' + attribute.name} className={classes.listItem}>
																<Typography>{parse(attribute.formatted)}</Typography>
															</ListItem>
														))}
													</List>
												) : column.key === 'partNumber' && item.datasheetLink ? (
													<>
														<Tooltip title={t('components.general.viewDatasheet')}>
															<div className={classes.tableRow}>
																<Link
																	className={classes.linkStyle}
																	color="#0000EE"
																	href={
																		domain +
																		middlePartOfRedirectLink +
																		queryString.stringify(datasheetTrackingParameters(item))
																	}
																	target="_blank"
																	onClick={() => {
																		pushToGTM(CLICK_DATASHEET_GA4_TAG);
																	}}>
																	<DescriptionIcon fontSize="small" /> {item[column.key]}
																</Link>
															</div>
														</Tooltip>
														{item.simPart !== '' ? (
															<LinkContainer>
																<Link
																	href={item.simPart}
																	target="_blank"
																	onClick={() => {
																		localClickOnSimilarProducts(icPartNumber, item.partNumber);
																		adobeOpenSimilarProducts(item.partNumber, item.simPart, 'Similar Products icon');
																	}}>
																	<SimilarProductsIcon alt="similarProductsIcon" height={16} width={16} />
																</Link>
																<Link
																	color="#2A78B8"
																	href={item.simPart}
																	target="_blank"
																	onClick={() => {
																		localClickOnSimilarProducts(icPartNumber, item.partNumber);
																		adobeOpenSimilarProducts(item.partNumber, item.simPart, 'Similar Products');
																	}}>
																	<Typography
																		variant="subtitle2"
																		style={{ marginBottom: 3.5, marginLeft: 1.5, fontWeight: 600 }}>
																		{t(`components.bom.simParts`)}
																	</Typography>
																</Link>
															</LinkContainer>
														) : null}
													</>
												) : column.key === 'description' ? (
													<Typography>{getDescription(item[column.key], domain)}</Typography>
												) : typeof item[column.key] === 'string' ? (
													<Typography>
														{i18n.exists(`components.bom.${parse(item[column.key])}`)
															? t(`components.bom.${parse(item[column.key])}`)
															: parse(item[column.key])}
													</Typography>
												) : (
													<Typography>{item[column.key]}</Typography>
												)}
											</TableCell>
										))}
									</TableRow>
								);
							})}

							{withTotalRow && (
								<TableRow>
									{totalRowValues.map((totalValue, index) => (
										<TableCell key={index} align={'left'} className={classes.totalValueTableCell}>
											{typeof totalValue === 'number' && !isNaN(totalValue) ? (
												<Typography>{Number.isInteger(totalValue) ? totalValue : totalValue.toFixed(2)}</Typography>
											) : (
												''
											)}
										</TableCell>
									))}
								</TableRow>
							)}
						</TableBody>
					</MuiTable>
				</BlurWrapper>
			</TableContainer>

			{footerContent && <div className={classes.footer}>{footerContent}</div>}
		</Paper>
	);
};

Table.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	title: PropTypes.string,
	actions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	footerContent: PropTypes.element,
	totalRow: PropTypes.bool,
	items: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default Table;
